import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Copy, Share2, Download, Users } from "lucide-react";

export default function Invite() {
  const qrRef = useRef(null);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/share-config"); setCfg(data); } catch {} })();
  }, []);

  // This web page always shares the public homepage (no per-user /invite link).
  const referral = "https://nikah-match.com";
  const shareMsg = cfg?.shareMessage || "Join me on Nikah Match — a halal way to a blessed marriage.";

  const copy = async () => { try { await navigator.clipboard.writeText(referral); toast.success("Invite link copied"); } catch { toast.error("Could not copy"); } };
  const share = async () => { try { if (navigator.share) await navigator.share({ title: "Nikah Match", text: shareMsg, url: referral }); else await copy(); } catch {} };
  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a"); link.download = "nikah-match-invite.png"; link.href = canvas.toDataURL("image/png"); link.click();
  };

  return (
    <div className="mx-auto max-w-lg text-center">
      <img src="/logo.webp" alt="Nikah Match" className="mx-auto h-32 w-32 rounded-2xl object-contain shadow-sm sm:h-40 sm:w-40" data-testid="invite-logo" />
      <h1 className="mt-5 font-serif text-4xl font-bold text-emerald-800">Share Nikah Match</h1>
      <p className="mx-auto mt-3 max-w-md text-slate-500">Help your family and friends discover a trusted, privacy-first platform designed for serious marriage.</p>

      <div className="mt-8 rounded-3xl border border-emerald-900/10 bg-white p-6 shadow-sm sm:p-8">
        <div ref={qrRef} className="mx-auto w-fit" data-testid="invite-qr">
          <QRCodeCanvas value={referral} size={216} fgColor="#0B4A3F" level="M" />
        </div>
        <button onClick={downloadQR} data-testid="invite-download-btn" className="mx-auto mt-5 flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-900">
          <Download className="h-4 w-4" /> Save QR code
        </button>

        <div className="mt-6 flex gap-2">
          <Input readOnly value={referral} data-testid="invite-link" className="flex-1 rounded-xl text-sm" />
          <button onClick={copy} data-testid="invite-copy-btn" className="rounded-xl border border-emerald-900/10 px-3 text-emerald-700 hover:bg-mint"><Copy className="h-4 w-4" /></button>
        </div>
        <button onClick={share} data-testid="invite-share-btn" className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-800 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-900"><Share2 className="h-4 w-4" /> Share invite</button>
      </div>

      <div className="mt-6 space-y-3">
        <a href={cfg?.appStoreUrl || "#"} target="_blank" rel="noreferrer" data-testid="invite-appstore" className="flex items-center justify-center gap-3 rounded-2xl bg-emerald-950 px-6 py-3.5 text-white transition-transform hover:-translate-y-0.5">
          <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor"><path d="M16.365 1.43c0 1.14-.417 2.2-1.11 2.98-.83.94-2.18 1.66-3.29 1.57-.14-1.11.42-2.28 1.09-3 .76-.82 2.07-1.44 3.31-1.55zM20.9 17.3c-.6 1.38-.89 2-1.66 3.22-1.08 1.7-2.6 3.82-4.49 3.83-1.67.02-2.1-1.09-4.37-1.08-2.27.01-2.74 1.1-4.42 1.08-1.88-.02-3.32-1.93-4.4-3.63C-.6 15.9-.9 9.87 1.58 6.7c1.16-1.51 2.99-2.47 4.71-2.47 1.75 0 2.85 1.09 4.3 1.09 1.4 0 2.26-1.09 4.29-1.09 1.53 0 3.15.83 4.31 2.27-3.79 2.08-3.17 7.49.71 8.8z"/></svg>
          <span className="text-left leading-tight"><span className="block text-[11px]">Download on the</span><span className="block text-lg font-semibold">App Store</span></span>
        </a>
        <a href={cfg?.playStoreUrl || "#"} target="_blank" rel="noreferrer" data-testid="invite-googleplay" className="flex items-center justify-center gap-3 rounded-2xl bg-emerald-950 px-6 py-3.5 text-white transition-transform hover:-translate-y-0.5">
          <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor"><path d="M3.6 1.8c-.3.3-.5.8-.5 1.4v17.6c0 .6.2 1.1.5 1.4l.1.1L14 12.1v-.2L3.7 1.7l-.1.1z"/></svg>
          <span className="text-left leading-tight"><span className="block text-[11px]">GET IT ON</span><span className="block text-lg font-semibold">Google Play</span></span>
        </a>
      </div>
      <p className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-400"><Users className="h-4 w-4" /> One account works across web, Android and iOS.</p>
    </div>
  );
}
